<template>
  <div class="modal" :style="{backgroundColor: bgColor}">
    <div class="modal__close" @click="closeHandler">
      <SvgIconCloseSvg :color="'var(--var-black-100)'" />
    </div>
    <div class="modal__body">
      <div class="card">
        <div v-if="typeItems === 'images'" class="card__images">
          <img :src="props.items[choosenItem]" />
        </div>
        <div v-if="typeItems === 'video'" class="card__video">
          <video
            :id="props.items[choosenItem][0]"
            :key="props.items[choosenItem][0]"
            :class="{
              'gallery-video-loop': true,
            }"
            controls
            autoplay
            loop
            playsinline
            muted
            :ref="'videoFirstRef'"
            draggable="false"
            @play="playHandler(props.items[choosenItem][0])"
            @canplaythrough="
              isLoadEnoughVideo(props.items[choosenItem][0])
            "
            @error="
              handErrorLoadVideo($event, props.items[choosenItem][0])
            "
          >
            <source
              :src="props.items[choosenItem][0]"
              type="video/mp4"
            />
          </video>
        </div>
      </div>
      <div
        v-if="isNeedAdditionalBlock"
        class="addition-block"
        @mousemove="dragging"
        @mousedown="dragStart"
        @mouseup="dragStop"
        @mouseleave="dragStop"
        @touchstart="dragStart"
        @touchmove="dragging"
        @touchend="dragStop"
        ref="itemsSliderRef"
      >
        <div
          class="addition-block__images"
          v-if="typeItems === 'images'"
        >
          <div
            v-for="(item, index) in items"
            class="image"
            :class="{item_active: choosenItem === index}"
          >
            <img
              :src="item"
              alt=""
              @click="chooseItemActive(images, index)"
            />
          </div>
        </div>
        <div
          class="addition-block__video-poster"
          v-if="typeItems === 'video' && videoRange?.length"
        >
          <div
            v-for="(item, index) in props.posters"
            class="video-poster"
            :class="{item_active: choosenItem === index}"
            :style="{
              display: videoRange?.includes(index) ? 'unset' : 'none',
            }"
          >
            <SvgBtnPlaySvg
              color="var(--var-blue-100)"
              @click="chooseItemActive(item.videoSrc, index)"
            />
            <img
              :src="item.value"
              alt=""
              @click="chooseItemActive(item.videoSrc, index)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="blackout" @click="closeHandler"></div>
</template>

<script setup lang="ts">
import {useBaseStore} from '~/modules/BaseModule/base.store';

const props = withDefaults(
  defineProps<{
    items: Array<string>;
    itemActive: number;
    posters: {videoSrc: string; value: string}[];
    typeItems: 'images' | 'video' | 'tour';
    videoRange?: number[];
    isNeedAdditionalBlock?: boolean;
    bgColor: string;
  }>(),
  {
    isNeedAdditionalBlock: true,
    bgColor: 'var(--var-white-000)',
  },
);
const emits = defineEmits(['close-action']);

const closeHandler = () => {
  emits('close-action', '');
};

const choosenItem = ref(0);
const base = useBaseStore();
const isMobile = computed(() => base.isMobile);
const positionY = ref(0);
const itemsSliderRef = ref(null);
const loadVideoForStart = ref<any>({});
let positionDiff: number;
let firstImgWidth: number;
let isDragStart = false;
let prevPageX: number;
let prevScrollLeft: number;
let scrollWidth: number;
let isDragging = false;

const playHandler = (currentVideo: string) => {
  const video = document.getElementById(currentVideo);
  if (video) {
    video.load();
  }
};

const isLoadEnoughVideo = (currentVideo: string) => {
  const video = document.getElementById(currentVideo);
  if (video) {
    setTimeout(() => {
      video.play();
    }, 1000);
  }
  loadVideoForStart.value = {
    ...loadVideoForStart.value,
    [currentVideo]: true,
  };
};

const handErrorLoadVideo = (e, video?: string) => {
  if (video) {
    const brokeVideo = document.getElementById(video);
    if (brokeVideo) {
      setTimeout(() => {
        brokeVideo.load();
        brokeVideo.play();
      }, 3000);
    }
  }
};

const chooseItemActive = (activeItem: string, index: number) => {
  if (!isDragging || !isMobile.value) {
    choosenItem.value = index;
    const choosenItemById = document.getElementById(activeItem);
    if (choosenItemById && props.typeItems === 'video') {
      setTimeout(() => {
        choosenItemById.play();
      }, 1000);
    }
  }
};

const dragging = (e) => {
  if (itemsSliderRef.value) {
    if (!isDragStart) return;
    e.preventDefault();
    isDragging = true;
    itemsSliderRef.value.classList.add('dragging');
    positionDiff = (e.pageX || e.touches[0].pageX) - prevPageX;
    itemsSliderRef.value.scrollLeft = prevScrollLeft - positionDiff;
  }
};
const autoSlide = () => {
  // if (videoRange.value?.length !== props.items?.length) {
  //   videoRange.value = Array.from(Array(props.items.length).keys());
  // }
  if (props.typeItems === 'video') {
    props.items.forEach((video, index) => {
      if (index >= 2) {
        const videoTag = document.getElementById(
          props.items[choosenItem.value][0],
        );
        videoTag.src = props.items[choosenItem.value][0];
      }
    });
  }
  if (
    (itemsSliderRef.value &&
      itemsSliderRef.value.scrollLeft -
        (itemsSliderRef.value.scrollWidth -
          itemsSliderRef.value.clientWidth) >
        -1) ||
    (itemsSliderRef.value && itemsSliderRef.value.scrollLeft <= 0)
  ) {
    return;
  }
  positionDiff = Math.abs(positionDiff);
  const additionalValueML = isMobile.value ? 10 : 14;
  let firstImgWidth =
    itemsSliderRef.value.clientWidth + additionalValueML;
  let valDifference = firstImgWidth - positionDiff;

  if (
    itemsSliderRef.value &&
    itemsSliderRef.value.scrollLeft > prevScrollLeft
  ) {
    return (itemsSliderRef.value.scrollLeft +=
      positionDiff > firstImgWidth / 5
        ? valDifference
        : -positionDiff);
  }
  if (itemsSliderRef.value) {
    return (itemsSliderRef.value.scrollLeft -=
      positionDiff > firstImgWidth / 5
        ? valDifference
        : -positionDiff);
  }
};

const dragStart = (e) => {
  if (itemsSliderRef.value) {
    isDragStart = true;
    prevPageX = e.pageX || e.touches[0].pageX;
    prevScrollLeft = itemsSliderRef.value.scrollLeft;
  }
};
const dragStop = (e) => {
  isDragStart = false;
  if (itemsSliderRef.value) {
    itemsSliderRef.value.classList.remove('dragging');
    if (!isDragging) return;
    setTimeout(() => {
      isDragging = false;
    }, 0);
  }
};

onMounted(() => {
  choosenItem.value = props.itemActive;
  positionY.value = window.pageYOffset;
  document.body.style.position = 'fixed';
  document.body.style.height = '100%';
  document.body.style.overflowY = 'unset';
  document.body.style.top = `-${positionY.value}px`;

  if (itemsSliderRef.value) {
    const additionalValueML = isMobile.value ? 10 : 14;
    firstImgWidth =
      itemsSliderRef.value.clientWidth + additionalValueML;
    scrollWidth =
      itemsSliderRef.value.scrollWidth -
      itemsSliderRef.value.clientWidth;
  }
});
onUnmounted(() => {
  document.body.style.removeProperty('position');
  document.body.style.removeProperty('top');
  document.body.style.removeProperty('height');
  document.body.style.overflowY = 'scroll';
  window.scroll(0, positionY.value || 0);
  positionY.value = 0;
});
</script>

<style scoped lang="scss">
.modal {
  position: fixed;
  display: flex;
  justify-content: space-around;
  align-items: center;
  z-index: 100000;
  top: 5vh;
  left: 2%;
  min-height: 90vh;
  max-height: 90vh;
  min-width: 96%;
  max-width: 96%;
  border-radius: 10px;

  &__body {
    width: 100%;
    display: flex;
    padding: 2%;
    gap: 20px;
    .card {
      width: 100%;
      margin: auto;
      &__images {
        width: 100%;
        img {
          width: 100%;
          border-radius: $br-2;
          height: 100%;
          max-height: 80vh;
          object-fit: contain;
        }
      }
      &__video {
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        video {
          object-fit: fill;
          height: 100%;
          max-height: 80vh;
          min-height: 80vh;
          border-radius: $br-2;

          @media (max-width: 1500px) {
            min-height: 63vh;
          }
          @media (max-width: 1350px) {
            min-height: 58vh;
          }
          @media (max-width: 1200px) {
            min-height: 50vh;
          }
          @media (max-width: 900px) {
            min-height: 40vh;
          }
          @media (max-width: 600px) {
            min-height: 25vh;
          }
        }
      }

      @media (max-width: $md) {
        padding: $p-2 !important;
      }
    }
    .addition-block {
      width: 30%;
      &__images {
        display: flex;
        justify-content: flex-start;
        flex-wrap: wrap;
        gap: 10px;
        .image {
          min-width: 90px;
          img {
            cursor: pointer;
            border-radius: $br-2;
            height: 55px;
            object-fit: contain;
          }
        }
      }
      &__video-poster {
        display: flex;
        justify-content: flex-start;
        flex-wrap: wrap;
        gap: 10px;
        .video-poster {
          min-width: 90px;
          position: relative;
          svg {
            position: absolute;
            top: 24%;
            left: 30%;
            height: 30px;
            cursor: pointer;
          }
          img {
            cursor: pointer;
            border-radius: $br-2;
            height: 55px;
            object-fit: contain;
          }
        }
      }
      @media (max-width: $lg) {
        padding: 16px 0px 0px 0px;
      }
      @media (max-width: $md) {
        position: absolute;
        display: flex;
        flex-wrap: nowrap;
        bottom: 30px;
        width: calc(100% - 20px);
        padding: 0px 10px;
        overflow: hidden;
        z-index: 10000;
        &__images {
          flex-wrap: nowrap;
          justify-content: left;
        }
        &__video-poster {
          flex-wrap: nowrap;
          justify-content: left;
        }
      }
    }
    @media (max-width: $md) {
      flex-direction: column;
    }
  }

  &__close {
    cursor: pointer;
    position: absolute;
    right: 10px;
    top: 10px;
    background-color: var(--var-white-000);
    display: flex;
    justify-content: center;
    align-items: center;
    height: 40px;
    width: 40px;
    border-radius: 50%;
    z-index: 100000;
    box-shadow: 0px 0px 8px 0px rgba(24, 25, 26, 0.08),
      0px 0px 16px 0px rgba(24, 25, 26, 0.12),
      0px 0px 32px 0px rgba(24, 25, 26, 0.16);

    svg {
      width: 18px;
      height: 18px;
    }
  }

  @media (max-width: $md) {
    min-width: 100%;
    min-height: 100%;
    left: 0;
    top: 0;
    border-radius: 0px;
  }
}
.blackout {
  position: fixed;
  background: var(--var-black-000);
  opacity: 0.7;
  z-index: 10000;
  top: 0;
  left: 0;
  min-width: 100vw;
  min-height: 100vh;
}
.item_active {
  border-radius: $br-3;
  border: 5px solid var(--var-blue-100);
  height: fit-content;
  height: 59px;
  img {
    height: 50px !important;
  }
}
.home-enter-active,
.home-leave-active {
  transition: height 1s;
}
.home-enter,
.home-leave-active {
  height: 100%;
}
</style>
